import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Container, Flex, Heading, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import HeroSection from './HeroSection';
import EssentialInfoSection from './EssentialInfoSection';
import DayTimelineSection from './DayTimelineSection';
import ExperienceSection from './ExperienceSection';
import StatisticsSection from './StatisticsSection';
import YouTubeMasonrySection from './YouTubeMasonrySection';
import WeeksSection from './WeeksSection';
import LocationSection from './LocationSection';
import PricingSection from './PricingSection';
import PastiSection from './PastiSection';
import InfoSection from './InfoSection';
import FAQSection from './FAQSection';
import StickyCTA from './StickyCTA';
import ContactForm from '../ContactForm';
import {
  CIL,
  VIDEO_URL,
  SECTION_TITLE_CONTAINER_BG,
  SECTION_TITLE_TEXT_COLOR,
} from './constants';
import { getRegistrationOpenState } from '../../utils';
import { shouldUseGuestBookingFlow } from '../../utils/guestEventEligibility';

const HERO_YOUTUBE_VIDEO_ID = 'bAX4jFOFW7E';
const HERO_VIDEO_EMBED_URL = `https://www.youtube.com/embed/${HERO_YOUTUBE_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${HERO_YOUTUBE_VIDEO_ID}&controls=0&showinfo=0&modestbranding=1&playsinline=1`;
const HERO_USE_VIDEO_MP4 =
  !HERO_YOUTUBE_VIDEO_ID &&
  VIDEO_URL &&
  (VIDEO_URL.endsWith('.mp4') || VIDEO_URL.startsWith('/videos/'));

const BASE_QUICK_NAV_ITEMS = [
  { id: 'section-weeks', labelKey: 'weeks' },
  { id: 'section-experience', label: "L'esperienza" },
  { id: 'section-quote', label: 'Quote 2026' },
  { id: 'section-day', label: 'Una giornata tipo' },
  { id: 'section-pasti', label: 'Pasti' },
  { id: 'section-munlab', label: 'Il Munlab' },
  { id: 'section-info-faq', label: 'Info & FAQ' },
  { id: 'section-contatti', label: 'Contatti' },
];

const CentroEstivoPage = ({
  event,
  onOpenDrawer,
  onListaAttesaClick,
  userData,
}) => {
  const navigate = useNavigate();
  const sectionRefs = useRef([]);
  const rafIdRef = useRef(null);
  const heroFadeEndPxRef = useRef(1);
  const [pageScrollHeight, setPageScrollHeight] = useState(() =>
    typeof document !== 'undefined' ? document.documentElement.scrollHeight : 0
  );
  const [bgVideoOpacity, setBgVideoOpacity] = useState(1);

  useEffect(() => {
    const updateHeight = () =>
      setPageScrollHeight(document.documentElement.scrollHeight);
    updateHeight();
    window.addEventListener('resize', updateHeight);
    const ro = new ResizeObserver(updateHeight);
    ro.observe(document.body);
    return () => {
      window.removeEventListener('resize', updateHeight);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    const clamp01 = (v) => Math.min(1, Math.max(0, v));

    const updateFadeMetrics = () => {
      const heroEl = sectionRefs.current?.[0];
      const heroH = heroEl?.offsetHeight ?? 0;
      heroFadeEndPxRef.current = Math.max(1, heroH);
    };

    const updateOpacityFromScroll = () => {
      const endPx = heroFadeEndPxRef.current || 1;
      const y = window.scrollY || 0;
      const t = clamp01(y / endPx);
      setBgVideoOpacity(1 - t);
    };

    const onScroll = () => {
      if (rafIdRef.current != null) return;
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        updateOpacityFromScroll();
      });
    };

    updateFadeMetrics();
    updateOpacityFromScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateFadeMetrics);

    const ro = new ResizeObserver(() => {
      updateFadeMetrics();
      updateOpacityFromScroll();
    });
    ro.observe(document.body);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateFadeMetrics);
      ro.disconnect();
      if (rafIdRef.current != null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  const quickNavItems = useMemo(() => {
    const n = event?.Varianti?.length ?? 0;
    const weeksLabel = n ? `Le ${n} settimane` : 'Le settimane';
    return BASE_QUICK_NAV_ITEMS.map((item) =>
      item.labelKey === 'weeks'
        ? { id: item.id, label: weeksLabel }
        : { id: item.id, label: item.label }
    );
  }, [event?.Varianti?.length]);

  const [now, setNow] = useState(() => Date.now());
  const registrationOpensAt = event?.aperturaIscrizioniAt
    ? new Date(event.aperturaIscrizioniAt).getTime()
    : null;
  const [availableDiscounts, setAvailableDiscounts] = useState([]);

  useEffect(() => {
    let cancelled = false;

    if (!userData?.id) {
      setAvailableDiscounts([]);
    } else {
      const fetchDiscounts = async () => {
        try {
          const response = await fetch('/api/user/discounts');
          if (!cancelled && response.ok) {
            const data = await response.json();
            setAvailableDiscounts(data.discounts || []);
          }
        } catch {
          if (!cancelled) setAvailableDiscounts([]);
        }
      };

      fetchDiscounts();
    }

    return () => {
      cancelled = true;
    };
  }, [userData?.id]);

  const { registrationOpen, isEarlyAccessOnly } = event
    ? getRegistrationOpenState(event, userData, availableDiscounts)
    : { registrationOpen: false, isEarlyAccessOnly: false };

  const isRegistrationOpen = registrationOpen;

  const guestFlowActive = shouldUseGuestBookingFlow(event, userData, {
    registrationOpen,
    isEarlyAccessOnly,
  });

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    interval.unref?.();
    return () => clearInterval(interval);
  }, []);

  const handleIscriviti = useCallback(() => {
    if (onOpenDrawer) {
      onOpenDrawer();
    } else {
      navigate('/event/centro-estivo-2026?openDrawer=1');
    }
  }, [onOpenDrawer, navigate]);

  const handleSettimanaClick = useCallback(() => {
    if (onOpenDrawer) {
      onOpenDrawer();
    } else {
      navigate('/event/centro-estivo-2026?openDrawer=1');
    }
  }, [onOpenDrawer, navigate]);

  return (
    <Box position="relative" minH="100vh" pb={{ base: 20, md: 0 }}>
      {/* Video fisso come sfondo su tutta la pagina */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={0}
        overflow="hidden"
        pointerEvents="none"
        opacity={bgVideoOpacity}
        transition="opacity 120ms linear"
        willChange="opacity"
      >
        {/* Video fisso su tutta la pagina */}
        <Box
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          overflow="hidden"
          sx={{ pointerEvents: 'none' }}
        >
          {HERO_USE_VIDEO_MP4 ? (
            <Box
              as="video"
              src={VIDEO_URL}
              autoPlay
              muted
              loop
              playsInline
              w="100%"
              h="100%"
              objectFit="cover"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                minWidth: '100%',
                minHeight: '100%',
                width: 'auto',
                height: 'auto',
                transform: 'translate(-50%, -50%)',
              }}
            />
          ) : (
            <iframe
              title="Hero video di sfondo"
              src={HERO_VIDEO_EMBED_URL}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '100vw',
                height: '56.25vw',
                minHeight: '100%',
                minWidth: '177.78vh',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            />
          )}
        </Box>
      </Box>

      {/* Overlay scuro a gradiente sopra al video: 0% in cima, nero pieno al footer */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        height={`${pageScrollHeight}px`}
        zIndex={1}
        pointerEvents="none"
        bg="linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%)"
      />

      <Box position="relative" zIndex={2}>
        <Container maxW="container.xl" mx="auto" px={{ base: 4, md: 6, lg: 8 }}>
          <Box
            ref={(el) => {
              sectionRefs.current[0] = el;
            }}
          >
            <HeroSection
              onIscriviti={handleIscriviti}
              event={event}
              registrationOpensAt={registrationOpensAt}
              now={now}
              isRegistrationOpen={isRegistrationOpen}
              userData={userData}
              guestFlowActive={guestFlowActive}
            />
          </Box>
          <Box
            as="nav"
            aria-label="Navigazione rapida sezioni"
            w="100%"
            maxW="container.lg"
            mx="auto"
            py={4}
            px={{ base: 3, md: 4 }}
            bg="white"
            borderBottomWidth="1px"
            borderColor="gray.100"
            borderRadius="xl"
          >
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              gap={3}
              w="100%"
            >
              {quickNavItems.map((item) => (
                <Box
                  key={item.id}
                  as="a"
                  href={`#${item.id}`}
                  px={3}
                  py={2}
                  borderRadius="md"
                  fontSize="sm"
                  fontWeight="semibold"
                  color={CIL.plum}
                  bg="white"
                  borderWidth="1px"
                  borderColor="gray.200"
                  boxShadow="sm"
                  _hover={{ bg: CIL.plumLight }}
                >
                  {item.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            ref={(el) => {
              sectionRefs.current[1] = el;
            }}
          >
            <EssentialInfoSection />
          </Box>
          <Box
            ref={(el) => {
              sectionRefs.current[2] = el;
            }}
          >
            <WeeksSection
              event={event}
              onSettimanaClick={handleSettimanaClick}
              onListaAttesaClick={onListaAttesaClick}
              isRegistrationOpen={isRegistrationOpen}
              userData={userData}
              guestFlowActive={guestFlowActive}
            />
          </Box>
          <Box
            ref={(el) => {
              sectionRefs.current[3] = el;
            }}
          >
            <ExperienceSection />
          </Box>
          <Box
            ref={(el) => {
              sectionRefs.current[4] = el;
            }}
          >
            <PricingSection event={event} />
          </Box>
          <Box
            ref={(el) => {
              sectionRefs.current[5] = el;
            }}
          >
            <DayTimelineSection />
          </Box>
          <Box
            ref={(el) => {
              sectionRefs.current[6] = el;
            }}
          >
            <PastiSection />
          </Box>
          <Box
            ref={(el) => {
              sectionRefs.current[7] = el;
            }}
          >
            <StatisticsSection />
          </Box>
          <Box
            ref={(el) => {
              sectionRefs.current[8] = el;
            }}
          >
            <YouTubeMasonrySection />
          </Box>
          <Box
            ref={(el) => {
              sectionRefs.current[9] = el;
            }}
          >
            <LocationSection />
          </Box>
          <Box
            ref={(el) => {
              sectionRefs.current[10] = el;
            }}
            as="section"
            id="section-info-faq"
            w="100%"
            maxW="100%"
            pt={{ base: 14, md: 20 }}
            px={{ base: 4, md: 6, lg: 8 }}
            scrollMarginTop={{ base: '80px', md: '140px' }}
          >
            {/* Mobile: titolo → contenuto → titolo → contenuto, con sfondo chiaro sui contenuti */}
            <Box display={{ base: 'block', md: 'none' }}>
              <Heading as="h2" size="lg" textAlign="center" mb={4}>
                <Box
                  w="100%"
                  px={4}
                  py={3}
                  borderRadius="lg"
                  sx={{ background: SECTION_TITLE_CONTAINER_BG }}
                  boxShadow="sm"
                >
                  <Text
                    as="span"
                    fontWeight="bold"
                    color={SECTION_TITLE_TEXT_COLOR}
                  >
                    Informazioni pratiche
                  </Text>
                </Box>
              </Heading>
              <Box
                bg="white"
                borderRadius="xl"
                boxShadow="sm"
                borderWidth="1px"
                borderColor="gray.100"
                py={4}
                px={0}
                mb={6}
              >
                <InfoSection event={event} hideTitle />
              </Box>
              <Heading as="h2" size="lg" textAlign="center" mb={4}>
                <Box
                  w="100%"
                  px={4}
                  py={3}
                  borderRadius="lg"
                  sx={{ background: SECTION_TITLE_CONTAINER_BG }}
                  boxShadow="sm"
                >
                  <Text
                    as="span"
                    fontWeight="bold"
                    color={SECTION_TITLE_TEXT_COLOR}
                  >
                    Domande frequenti
                  </Text>
                </Box>
              </Heading>
              <Box
                bg="white"
                borderRadius="xl"
                boxShadow="sm"
                borderWidth="1px"
                borderColor="gray.100"
                py={4}
                px={0}
              >
                <FAQSection event={event} hideTitle />
              </Box>
            </Box>

            {/* Desktop: due colonne (titoli in riga, contenuti in riga con sfondo grigio) */}
            <Box display={{ base: 'none', md: 'block' }}>
              <Flex direction="row" gap={8} mb={8}>
                <Heading as="h2" size="lg" textAlign="left" flex={1}>
                  <Box
                    w="100%"
                    px={4}
                    py={3}
                    borderRadius="lg"
                    sx={{ background: SECTION_TITLE_CONTAINER_BG }}
                    boxShadow="sm"
                  >
                    <Text
                      as="span"
                      fontWeight="bold"
                      color={SECTION_TITLE_TEXT_COLOR}
                    >
                      Informazioni pratiche
                    </Text>
                  </Box>
                </Heading>
                <Heading as="h2" size="lg" textAlign="left" flex={1}>
                  <Box
                    w="100%"
                    px={4}
                    py={3}
                    borderRadius="lg"
                    sx={{ background: SECTION_TITLE_CONTAINER_BG }}
                    boxShadow="sm"
                  >
                    <Text
                      as="span"
                      fontWeight="bold"
                      color={SECTION_TITLE_TEXT_COLOR}
                    >
                      Domande frequenti
                    </Text>
                  </Box>
                </Heading>
              </Flex>
              <Flex
                direction="row"
                gap={8}
                align="stretch"
                py={8}
                bg="gray.50"
                borderRadius="2xl"
              >
                <Box flex={1} minW={0}>
                  <InfoSection event={event} hideTitle />
                </Box>
                <Box flex={1} minW={0}>
                  <FAQSection event={event} hideTitle />
                </Box>
              </Flex>
            </Box>
          </Box>
          <Box
            ref={(el) => {
              sectionRefs.current[11] = el;
            }}
            as="section"
            id="section-contatti"
            w="100%"
            maxW="100%"
            py={{ base: 10, md: 14 }}
            px={{ base: 4, md: 6, lg: 8 }}
            scrollMarginTop={{ base: '80px', md: '140px' }}
          >
            <Heading
              as="h2"
              size="lg"
              mb={{ base: 6, md: 8 }}
              textAlign="center"
            >
              <Box
                w="100%"
                px={4}
                py={3}
                borderRadius="lg"
                sx={{ background: SECTION_TITLE_CONTAINER_BG }}
                boxShadow="sm"
              >
                <Text
                  as="span"
                  fontWeight="bold"
                  color={SECTION_TITLE_TEXT_COLOR}
                >
                  Ancora qualche dubbio?
                </Text>
              </Box>
            </Heading>
            <Container
              maxW={{ base: 'container.lg', md: 'container.xl' }}
              px={{ base: 4, md: 8 }}
            >
              <ContactForm
                title=""
                description="Compila il modulo e ti risponderemo al più presto!"
                titleSize="lg"
                variant="light"
                splitLayout
              />
            </Container>
          </Box>
          <StickyCTA
            onIscriviti={handleIscriviti}
            isRegistrationOpen={isRegistrationOpen}
            userData={userData}
          />
        </Container>
      </Box>
    </Box>
  );
};

export default CentroEstivoPage;
